<template>
  <RevTable
    :collection="pricingRules"
    :columns="tableColumns"
    transparent-header
  >
    <template #body-category="{ item }">
      {{ getCategoryName(item.category) }}</template
    >
    <template #body-fixedAmount="{ item }">
      {{ i18n.price(item.offset) }}</template
    >
    <template #body-rule="{ item }">
      {{
        i18n(translations.rulePrice, {
          country: item.sourceMarket,
          multiplier: i18n.number(item.multiplier),
          offset: i18n.price(item.offset),
          sign: Number(item.offset.amount) >= 0 ? '+' : '',
        })
      }}
    </template>
    <template #body-updatePrices="{ item }">
      <span v-if="isRuleInProgress(item)" class="text-static-danger-hi">
        {{ i18n(translations.updateInProgress) }}
      </span>

      <RevLink v-else @click="handleApplyPricingRule(item)">
        {{ i18n(translations.updatePrices) }}
      </RevLink>
    </template>
    <template #body-action="{ item }">
      <RevContextualMenu position="bottom-end">
        <template #trigger="{ show }">
          <RevButtonIcon
            :alternative-text="i18n(translations.actions)"
            :icon="IconMoreVertical"
            variant="secondary"
            @click="show"
          />
        </template>

        <template #items>
          <RevListItemInteractive @click="emit('edit', item)">
            <template #label>{{ i18n(translations.edit) }}</template>
          </RevListItemInteractive>
          <RevListItemInteractive @click="handleOnDelete(item)">
            <template #label>{{ i18n(translations.delete) }}</template>
          </RevListItemInteractive>
        </template>
      </RevContextualMenu>
    </template>
  </RevTable>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'

import {
  type PricingRule,
  deletePricingRule,
  postPricingRuleRunTask,
} from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevContextualMenu } from '@ds/components/ContextualMenu'
import { RevLink } from '@ds/components/Link'
import { RevListItemInteractive } from '@ds/components/ListItemInteractive'
import { RevTable } from '@ds/components/Table'
import { IconMoreVertical } from '@ds/icons/IconMoreVertical'

import translations from './PricingRulesList.translations'

const i18n = useI18n()
const { openErrorToast } = useTheToast()
const { trackClick } = useTracking()

const emit = defineEmits(['update', 'edit'])
const props = defineProps<{
  pricingRules: PricingRule[]
}>()

const tableColumns = [
  {
    key: 'category',
    label: i18n(translations.category),
  },
  {
    key: 'multiplier',
    label: i18n(translations.multiplier),
  },
  {
    key: 'fixedAmount',
    label: i18n(translations.fixedAmount),
  },
  {
    key: 'rule',
    label: i18n(translations.rule),
  },
  {
    key: 'updatePrices',
    label: '',
  },
  {
    key: 'action',
    label: '',
  },
]

function getCategoryName(
  category: { id: number; name: string } | null,
): string {
  if (category?.name) return category.name

  return i18n(
    props.pricingRules.length === 1
      ? translations.allCategories
      : translations.otherCategories,
  )
}

function isRuleInProgress(rule: PricingRule) {
  if (!rule.lastTaskCreationDate) {
    return false
  }

  const MIN_HOURS_TO_WAIT_FOR_UPDATE = 1
  const diffInHours = Math.abs(
    (new Date().getTime() - new Date(rule.lastTaskCreationDate).getTime()) /
      36e5,
  )

  return diffInHours < MIN_HOURS_TO_WAIT_FOR_UPDATE
}

async function handleApplyPricingRule(rule: PricingRule) {
  trackClick({
    zone: 'pricing_tool',
    name: 'update_price',
    value: {
      sourceMarket: rule.sourceMarket,
      targetMarket: rule.targetMarket,
      categoryId: rule.category?.id,
    },
  })

  try {
    await $httpFetch(postPricingRuleRunTask, {
      pathParams: {
        ruleId: rule.id,
      },
    })

    emit('update')
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as unknown as Error),
    })
  }
}

async function handleOnDelete(rule: PricingRule) {
  try {
    await $httpFetch(deletePricingRule, {
      pathParams: {
        ruleId: rule.id,
      },
    })

    emit('update')
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as unknown as Error),
    })
  }
}
</script>
